import React, { useEffect, useState } from "react";
import { Skeleton } from "@mui/material";

export default function DynamicTable({
                                         data,
                                         columns,
                                         highlightConfig = {},
                                         loading = false,
                                         onDelete,
                                         onSave,
                                         editableColumns = [],
                                         isEditableColumn,
                                         requiredOnAdd = [],
                                         onToggleStatus,
                                         onAdd,
                                         onEdit,
                                         onRowClick,
                                         showDetailsColumn = true,
                                         detailsColumnLabel = "تفاصيل",
                                     }) {
    const [editId, setEditId] = useState(null);
    const [editRow, setEditRow] = useState({});
    const [recentlyAddedId, setRecentlyAddedId] = useState(null); // Track the newly added row
    const [highlightedRowId, setHighlightedRowId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleEdit = (row) => {
        setEditId(row.id);
        setEditRow({ ...row });
    };

    const handleChange = (key, value) => {
        setEditRow((prev) => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        setIsLoading(true); // Start loading
        try {
            await onSave(editRow.id, editRow); // Assuming onSave is an async function that sends data to the backend
            setEditId(null);
            setEditRow({});
        } catch (error) {
            console.error("Error saving:", error);
        } finally {
            setIsLoading(false); // Stop loading
        }
    };

    const handleCancel = () => {
        if (editId && editId.toString().startsWith("temp-")) {
            // Remove the temporary row (empty row) if it was added
            setEditId(null);
            setEditRow({});
            setRecentlyAddedId(null); // Remove the temporary row from the data
        } else {
            setEditId(null);
            setEditRow({});
        }
    };

    useEffect(() => {
        if (recentlyAddedId) {
            const newRow = data.find((d) => d.id === recentlyAddedId);
            if (newRow) {
                setEditRow({ ...newRow });
                setEditId(newRow.id);
                setHighlightedRowId(newRow.id);
                const timer = setTimeout(() => {
                    setHighlightedRowId(null);
                }, 9000);
                return () => clearTimeout(timer);
            }
        }
    }, [recentlyAddedId, data]);

    const getCellContent = (col, value) => {
        if (highlightConfig[col]) {
            const color = highlightConfig[col][value] || "#ccc";
            return (
                <span style={{ ...styles.badge, background: color, color: "#fff" }}>
                    {value}
                </span>
            );
        }
        return value;
    };

    const handleAdd = async () => {
        setIsLoading(true); // Set loading state to true when adding a new row
        try {
            await onAdd(setRecentlyAddedId); // Assuming onAdd is an async function that adds the new item
        } catch (error) {
            console.error("Error adding new item:", error);
        } finally {
            setIsLoading(false); // Stop loading once the add operation is complete
        }
    };

    return (
        <div style={styles.wrapper} dir="rtl">
            <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined" rel="stylesheet" />

            {onAdd && (
                <div style={styles.headerBar}>
                    <button
                        style={{
                            ...styles.addButton,
                            opacity: editId ? 0.5 : 1,
                            cursor: editId ? "not-allowed" : "pointer",
                        }}
                        onClick={() => {
                            if (!editId) handleAdd(); // Call handleAdd to trigger adding a new row
                        }}
                    >
                        <span className="material-symbols-outlined" style={styles.iconAdd}>add</span>
                    </button>
                </div>
            )}

            <table style={styles.table}>
                <thead>
                <tr>
                    {columns.map((col) => (
                        <th key={col.key} style={styles.th}>{col.label}</th>
                    ))}
                    {showDetailsColumn && <th style={styles.th}>{detailsColumnLabel}</th>}
                    {editableColumns && editableColumns.length > 0 && <th style={styles.th}>تعديل</th>}
                    <th style={styles.th}>حذف</th>
                </tr>
                </thead>
                <tbody>
                {isLoading || loading ? (
                    [...Array(3)].map((_, i) => (
                        <tr key={i}>
                            {columns.map((col, idx) => (
                                <td key={idx} style={styles.td}><Skeleton variant="text" width="100%" height={30} /></td>
                            ))}
                            {showDetailsColumn && (
                                <td style={styles.td}><Skeleton variant="circular" width={24} height={24} /></td>
                            )}
                            {editableColumns && editableColumns.length > 0 && (
                                <td style={styles.td}><Skeleton variant="circular" width={24} height={24} /></td>
                            )}
                            <td style={styles.td}><Skeleton variant="circular" width={24} height={24} /></td>
                        </tr>
                    ))
                ) : data.length === 0 ? (
                    <tr>
                        <td colSpan={columns.length + 3} style={styles.td}>لا توجد بيانات</td>
                    </tr>
                ) : (
                    data.map((row, idx) => {
                        const isEditing = row.id === editId;
                        return (
                            <tr key={row.id || idx} style={{ backgroundColor: highlightedRowId === row.id ? "#f3fdfb" : "transparent" }}>
                                {columns.map((col) => (
                                    <td key={col.key} style={styles.td}>
{isEditing && editableColumns.includes(col.key) &&
    (!col.editableIf || col.editableIf(row)) && 
    (!isEditableColumn || isEditableColumn(col.key, row)) ? (
                                            col.isDropdown ? (
                                                <select
                                                    style={styles.input}
                                                    value={editRow[col.key]}
                                                    onChange={(e) => handleChange(col.key, e.target.value)}
                                                >
                                                    <option value="">اختر</option>
                                                    {col.options?.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type={col.isTime ? "time" : col.isNumber ? "number" : "text"}
                                                    min={col.key === 'servicePeriod' ? 5 : 0}
                                                    style={styles.input}
                                                    value={editRow[col.key] || ""}
                                                    onChange={(e) => handleChange(col.key, e.target.value)}
                                                />
                                            )
                                        ) : col.render ? (
                                            col.render(row)
                                        ) : (
                                            getCellContent(col.key, row[col.key])
                                        )}
                                    </td>
                                ))}
                                {showDetailsColumn && (
                                    <td style={styles.td}>
                                        {!isEditing && (
                                            <button style={styles.iconBtn} onClick={() => onEdit?.(row)} >
                                                <span className="material-symbols-outlined" style={styles.iconEye}>visibility</span>
                                            </button>
                                        )}
                                    </td>
                                )}
                                {editableColumns && editableColumns.length > 0 && (
                                    <td style={styles.td}>
                                        {isEditing ? (
                                            <div style={{ display: "flex", gap: "10px" }}>
                                                <button style={styles.saveBtn} onClick={handleSave}>حفظ</button>
                                                <button style={styles.cancelBtn} onClick={() => handleCancel()}>إلغاء</button>
                                            </div>
                                        ) : (
                                            <button style={styles.iconBtn} onClick={() => handleEdit(row)} >
                                                <span className="material-symbols-outlined" style={styles.iconEdit}>edit</span>
                                            </button>
                                        )}
                                    </td>
                                )}
                                <td style={styles.td}>
                                    {!isEditing && (
                                        <button style={styles.iconBtn} onClick={() => {
                                            console.log(row)
                                            onDelete(row.id)
                                        }} >
                                            <span className="material-symbols-outlined" style={styles.iconDelete}>delete</span>
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })
                )}
                </tbody>
            </table>
        </div>
    );
}

const styles = {
    wrapper: {
        width: "100%",
        padding: "20px",
        borderRadius: "10px",
        border: "1px solid var(--clr-border)",
        background: "#fff",
        overflowX: "auto",
    },
    headerBar: {
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: "10px",
    },
    addButton: {
        backgroundColor: "#01c8aa",
        color: "#fff",
        border: "none",
        borderRadius: "50%",
        width: "36px",
        height: "36px",
        fontSize: "24px",
        cursor: "pointer",
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    iconAdd: {fontSize: "24px", fontWeight: "bold"},
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "14px",
    },
    th: {
        textAlign: "right",
        padding: "14px",
        background: "#f9f9f9",
        borderBottom: "1px solid var(--clr-border)",
    },
    td: {
        textAlign: "right",
        padding: "14px",
        borderBottom: "1px solid var(--clr-border)",
        verticalAlign: "top",
    },
    input: {
        width: "100%",
        padding: "6px 10px",
        fontSize: "14px",
        borderRadius: "6px",
        border: "1px solid var(--clr-border)",
        fontFamily: "inherit",
    },
    badge: {
        padding: "6px 12px",
        borderRadius: "15px",
        fontSize: "13px",
        display: "inline-block",
        textAlign: "center",
    },
    iconBtn: {
        border: "none",
        background: "transparent",
        fontSize: "18px",
        cursor: "pointer",
    },
    saveBtn: {
        fontSize: "14px",
        backgroundColor: "var(--clr-primary)",
        color: "#fff",
        border: "none",
        borderRadius: "4px",
        padding: "6px 16px",
        cursor: "pointer",
    },
    cancelBtn: {
        fontSize: "14px",
        backgroundColor: "#eee",
        border: "1px solid #ccc",
        borderRadius: "4px",
        padding: "6px 16px",
        cursor: "pointer",
    },
    iconEye: {
        fontSize: "22px",
        color: "#555",
        verticalAlign: "middle",
    },
    iconEdit: {
        fontSize: "20px",
        color: "#333",
    },
    iconDelete: {
        fontSize: "20px",
        color: "#c00",
    },
}
